import { GridColDef, GridRowParams } from "@mui/x-data-grid";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import MainApiService from "../../../../../services/mainApi.service";
import {
  AutoCompleteUncontrolled,
  ISelectType,
  OverflowTooltip,
  SearchInput,
  Table,
  useDebounce,
  useFetch,
} from "@4uhub/lib4uhub";
import { IProtocolsDefault } from "../../../../../models/protocols-default";
import { Box, Chip } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { listProfileAccessTicketDefaultDocCategories } from "../../../../../services/ticketDefaultDocCategory.service";

const PROTOCOLS_DEFAULT_ROUTE =
  process.env.REACT_APP_TICKETS + "/api/v1/TicketDefaultDoc/";
const protocolGenerateService = new MainApiService<IProtocolsDefault>(
  PROTOCOLS_DEFAULT_ROUTE
);

const translationPath = "page.settings.crm.protocolsDefault.";

const ProtocolsDefaultPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { eI } = useParams();
  const [text, setText] = useState<string>(
    () => localStorage.getItem("protocolDefaultSearchBy") ?? ""
  );
  const searchBy = useDebounce(text, 500);
  const [categories, setCategories] = useState<ISelectType[]>([]);
  const [selectedCategory, setSelectedCategory] = useState<ISelectType | null>(
    null
  );
  const { sendRequest: listCategoriesRequest } = useFetch(
    listProfileAccessTicketDefaultDocCategories
  );

  const fetchCategories = useCallback(async () => {
    const { data, success } = await listCategoriesRequest({});

    if (data && success) {
      const normalizedCategories = data.map((item) => ({
        ...item,
        code: item.code != null ? String(item.code) : undefined,
      }));

      setCategories(normalizedCategories);

      const savedId = localStorage.getItem(
        "protocolDefaultSelectedCategoryId"
      );
      if (savedId) {
        const found = normalizedCategories.find((opt) => opt.id === savedId);
        if (found) setSelectedCategory(found);
      }
    }
  }, [listCategoriesRequest]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const columns: GridColDef<IProtocolsDefault>[] = [
    {
      field: "title",
      headerName: t(translationPath + "title") || "Title",
      renderCell: (params) => {
        if (!params.value) return "-";
        return (
          <OverflowTooltip
            text={params.value}
            tooltip={params.value}
            variant="body2"
          />
        );
      },
    },
    {
      field: "message",
      headerName: t(translationPath + "message") || "Message",
      renderCell: (params) => {
        if (!params.value) return "-";
        return (
          <OverflowTooltip
            text={params.value}
            tooltip={params.value}
            variant="body2"
          />
        );
      },
    },
    {
      field: "ticketDefaultDocCategory",
      headerName: t(translationPath + "category") || "Category",
      minWidth: 220,
      renderCell: (params) =>
        params.value ? (
          <OverflowTooltip
            text={params.value.name}
            tooltip={params.value.name}
            variant="body2"
          />
        ) : (
          "-"
        ),
    },
    {
      field: "enable",
      headerName: t(translationPath + "status") || "Status",
      maxWidth: 150,
      renderCell: (params) => (
        <Chip
          size="small"
          label={
            params.value
              ? t(translationPath + "active")
              : t(translationPath + "inactive")
          }
          color={params.value ? "success" : "error"}
        />
      ),
    },
  ];

  const handleAdd = () => {
    navigate("new");
  };

  const handleRowClick = (params: GridRowParams<IProtocolsDefault>) => {
    navigate(`${params.id.toString()}/edit`);
  };

  const changeCategoryHandler = useCallback((value: ISelectType | null) => {
    setSelectedCategory(value);
    if (value?.id) {
      localStorage.setItem("protocolDefaultSelectedCategoryId", value.id);
    } else {
      localStorage.removeItem("protocolDefaultSelectedCategoryId");
    }
  }, []);

  const changeSearchHandler = useCallback((value: string | null) => {
    const v = (value ?? "").trim();
    setText(v);
    localStorage.setItem("protocolDefaultSearchBy", v);
  }, []);

  const serviceProps = useMemo(
    () => ({
      SearchBy: searchBy.length > 0 ? searchBy : undefined,
      TicketDefaultDocCategoryId: selectedCategory?.id,
    }),
    [searchBy, selectedCategory]
  );

  return (
    <Table<IProtocolsDefault>
      showDefaultMacroFilters={false}
      service={protocolGenerateService}
      columns={columns}
      title={t(translationPath + "protocols_default")}
      searchInput={false}
      serviceProps={serviceProps}
      onAdd={handleAdd}
      onRowClick={handleRowClick}
      macroFilters={() => (
        <>
          <Box
            sx={{
              width: "30%",
              minWidth: "450px",
            }}
          >
            <SearchInput
              onSearch={changeSearchHandler}
              placeholder={t(translationPath + "search")}
              focus={false}
              cleanButton
              defaultValue={text}
              fireOnMount={false}
            />
          </Box>
          <AutoCompleteUncontrolled
            label={t(translationPath + "category") || "Category"}
            onValueSelected={changeCategoryHandler}
            value={selectedCategory}
            options={categories}
            clearable={true}
            size="small"
            fullWidth
            sx={{ minWidth: 300 }}
          />
        </>
      )}
      backButton
      backButtonUrl={`/e/${eI}/dashboard/settings`}
      searchInputPlaceHolder={
        t(translationPath + "search") || "Search for a protocol default"
      }
      addButtonLabel={
        t(translationPath + "add_protocol_default") || "Add protocol default"
      }
      confirmOptions={{
        title: t(translationPath + "confirm.title"),
        description: t(translationPath + "confirm.description"),
        confirmationButtonProps: {
          variant: "contained",
        },
      }}
    />
  );
};

export default ProtocolsDefaultPage;
